import { interpolatingMedian } from "../../utils/median.js";
import { n as aggregateQrPose } from "../../qr-pose-aggregation-Ce3agYE2.js";
//#region ../src/ar/qr/qr-sighting-accumulator.ts
/**
* Folds a stream of QR detections into per-code SIGHTINGS — the bursts of
* detections that happen each time someone walks up to a printed code.
*
* WHY THIS EXISTS. Minting a code's world pose from a whole recording needs
* every time the code was seen, minutes apart. It cannot read that back from
* the detection slice: the recorder's ring holds the last 100 entries, which
* at the default cadence is about twelve seconds, so a three-minute walk with
* ten sightings has lost every early one by the time the session stops. So
* the fold happens as detections arrive, and memory stays O(sightings)
* rather than O(detections).
*
* WHAT A SIGHTING IS. A run of detections of the SAME code with no gap longer
* than `gapMs`. Standing at a poster for a minute is one sighting; walking a
* loop and coming back is two.
*
* THE ODOMETRY FRAME IS PART OF THE MODEL. A tracking restart or a loop
* closure changes the odometry frame while stored QR poses stay in the old
* one, so sightings on either side describe different frames. Comparing them
* would read as a moved poster — or worse, average two frames into a
* plausible-looking anchor. `noteFrameChange()` closes the open burst and
* starts a new SEGMENT, and `spansFrameChange()` lets the mint decline.
*
* SEE `GpsPlusSlamJs_Docs/docs/2026-08-28-0636-recorder-qr-anchor-authoring-plan.md`
* §3 M-B (DEC-3, DEC-4; cold-review blocker 4 and finding 11).
*/
/**
* Longest pause inside one sighting (ms).
*
* A guess until the field recordings measure the real distribution of
* inter-sighting gaps — that measurement is the probe's job. It only has to
* separate "still looking at it" from "walked a loop and came back", and the
* planned walks leave ~30 s between visits, so anything from a few seconds
* upward would do.
*/
const DEFAULT_SIGHTING_GAP_MS = 4e3;
/** Poses kept per burst for the robust aggregate. */
const DEFAULT_MAX_POSES_PER_SIGHTING = 32;
function isFinitePose(pose) {
	return pose.position.every((v) => Number.isFinite(v)) && pose.rotation.every((v) => Number.isFinite(v));
}
function createQrSightingAccumulator(options = {}) {
	const gapMs = options.gapMs ?? 4e3;
	const maxPoses = options.maxPosesPerSighting ?? DEFAULT_MAX_POSES_PER_SIGHTING;
	const open = /* @__PURE__ */ new Map();
	const closed = /* @__PURE__ */ new Map();
	let segment = 0;
	/** Build the closed sighting a burst would become, without ending it. */
	function summarize(text, burst) {
		const aggregate = aggregateQrPose(burst.poses);
		if (aggregate === null) return null;
		const sizes = [...burst.sizes].sort((a, b) => a - b);
		return {
			text,
			firstTimestamp: burst.first,
			lastTimestamp: burst.last,
			detectionCount: burst.detectionCount,
			posesUsed: burst.poses.length,
			odomPose: aggregate.pose,
			translationSpreadM: aggregate.translationSpreadM,
			rotationSpreadDeg: aggregate.rotationSpreadDeg,
			sizeM: interpolatingMedian(sizes),
			sizeSpreadM: sizes.length === 0 ? 0 : (sizes.at(-1) ?? 0) - (sizes[0] ?? 0),
			alignmentMatrix: burst.tail.alignmentMatrix,
			zero: burst.tail.zero,
			alignmentSampleCount: burst.tail.alignmentSampleCount,
			...burst.tail.gpsAccuracyM !== void 0 ? { gpsAccuracyM: burst.tail.gpsAccuracyM } : {},
			segment: burst.segment
		};
	}
	function close(text) {
		const burst = open.get(text);
		open.delete(text);
		if (burst === void 0) return;
		const sighting = summarize(text, burst);
		if (sighting === null) return;
		const list = closed.get(text) ?? [];
		list.push(sighting);
		closed.set(text, list);
	}
	return {
		observe(observation) {
			if (!Number.isFinite(observation.timestamp) || !Number.isFinite(observation.sizeM) || !isFinitePose(observation.odomPose)) return;
			const current = open.get(observation.text);
			if (current !== void 0 && observation.timestamp - current.last > gapMs) close(observation.text);
			const burst = open.get(observation.text);
			if (burst === void 0) {
				open.set(observation.text, {
					first: observation.timestamp,
					last: observation.timestamp,
					detectionCount: 1,
					poses: [observation.odomPose],
					sizes: [observation.sizeM],
					segment,
					tail: observation
				});
				return;
			}
			burst.last = Math.max(burst.last, observation.timestamp);
			burst.detectionCount += 1;
			if (observation.timestamp >= burst.tail.timestamp) burst.tail = observation;
			burst.poses.push(observation.odomPose);
			if (burst.poses.length > maxPoses) burst.poses.shift();
			burst.sizes.push(observation.sizeM);
			if (burst.sizes.length > maxPoses) burst.sizes.shift();
		},
		noteFrameChange() {
			for (const text of [...open.keys()]) close(text);
			segment += 1;
		},
		flush() {
			for (const text of [...open.keys()]) close(text);
		},
		sightings: (text) => closed.get(text) ?? [],
		sightingsIncludingOpen(text) {
			const list = closed.get(text) ?? [];
			const burst = open.get(text);
			if (burst === void 0) return list;
			const pending = summarize(text, burst);
			return pending === null ? list : [...list, pending];
		},
		codes: () => [...new Set([...closed.keys(), ...open.keys()])],
		hasOpenBurst: (text) => open.has(text),
		currentSegment: () => segment,
		spansFrameChange(text) {
			const list = closed.get(text) ?? [];
			const openSegment = open.get(text)?.segment;
			const segments = new Set(list.map((s) => s.segment));
			if (openSegment !== void 0) segments.add(openSegment);
			return segments.size > 1;
		},
		reset() {
			open.clear();
			closed.clear();
			segment = 0;
		}
	};
}
//#endregion
export { DEFAULT_SIGHTING_GAP_MS, createQrSightingAccumulator };
